import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { Fragment, ReactNode } from "react";
import { Nullable } from "../../../../../types/utils";
import { OrderCategory, OrderCostingVersion, OrderStatus, TOrderCostV6, } from "../../../../api/endpoints/Order";
import Col from "../../../../components/Common/Col";
import Row from "../../../../components/Common/Row";
import { serviceChargeCost, servicePackageCost, volumeBandCost } from "../../../../utils/Cost";
import { formatBinaryCapacityUnit, formatCurrency, formatDailyVolumeLimit } from "../../../../utils/Format";
import OrderHelpers from "../../../../utils/OrderHelpers";
import CostError from "../OrderCostSummaryV2/CostError";
import CostingVersion from "../OrderCostSummaryV2/CostingVersion";

interface IProps {
  cost: TOrderCostV6;
  currentCostingVersion?: Nullable<OrderCostingVersion>;
  orderCategory: OrderCategory;
  orderStatus: OrderStatus;
  annualTotalCostFootnoteRef?: string;
  additionalContent?: ReactNode;
}

const OrderCostSummaryV6 = (props: IProps) => {
  const { additionalContent, cost, orderCategory, currentCostingVersion, annualTotalCostFootnoteRef } = props;
  const { total_cost, error } = cost.data;

  if (error) {
    return <CostError error={error} />
  }

  if (!total_cost) {
    return null;
  }

  const isOverLimit = OrderHelpers.isOverVolumeLimit(cost, currentCostingVersion);

  return (
    <Fragment>
      {
        (orderCategory === OrderCategory.REALTIME || orderCategory === OrderCategory.EU_CUTOUT) &&
        <Fragment>
          <Row className="highlighted">
            <Col sm={12} md={10}>
              Estimated daily volume

              {
                isOverLimit && currentCostingVersion &&
                <div className="data-limit-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="icon" />

                  {formatDailyVolumeLimit(currentCostingVersion.max_average_daily_volume)} daily volume limit exceeded
                </div>
              }
            </Col>
            <Col sm={12} md={2}>
              {formatBinaryCapacityUnit(total_cost.average_daily_volume)}
            </Col>
          </Row>

          <Row className="highlighted">
            <Col sm={12} md={10}>
              Estimated annual volume
            </Col>
            <Col sm={12} md={2}>
              {formatBinaryCapacityUnit(total_cost.yearly_volume)}
            </Col>
          </Row>

          <Row>
            <Col sm={12} md={10}>
              Volume Band cost
            </Col>
            <Col sm={12} md={2}>
              {formatCurrency(volumeBandCost(cost))}
            </Col>
          </Row>

          <Row>
            <Col sm={12} md={10}>
              Service Package cost
            </Col>
            <Col sm={12} md={2}>
              {formatCurrency(servicePackageCost(cost))}
            </Col>
          </Row>
        </Fragment>
      }

      {
        (orderCategory === OrderCategory.ARCHIVE || orderCategory === OrderCategory.WEB) &&
        <Row>
          <Col sm={12} md={10}>
            Service Charge
          </Col>
          <Col sm={12} md={2}>
            {formatCurrency(serviceChargeCost(cost))}
          </Col>
        </Row>
      }


      <Row className="total-cost">
        <Col sm={12} md={10}>
          <strong>Annual total cost</strong>
          {annualTotalCostFootnoteRef && <sup>{annualTotalCostFootnoteRef}</sup>}
        </Col>
        <Col sm={12} md={2}>
          {formatCurrency(serviceChargeCost(cost))}
        </Col>
      </Row>

      {additionalContent}

      {
        cost.data.ecquote_version &&
        <CostingVersion version={currentCostingVersion} ecquoteVersion={cost.data.ecquote_version} />
      }
    </Fragment>
  )
}

export default OrderCostSummaryV6;
